import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import LoanTabLayout from 'app/pages/loanApplication/LoanTabLayout';
import _ from 'lodash';

import useDebounce from 'hooks/useDebounce';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  resetLoanRequestsAction,
  searchLoanRequestGroupsAction,
  selectLoanRequestGroups,
  selectLoanRequestsIsLoading,
  selectLoanRequestsTotalCount,
} from 'store/loanRequests';
import { getUsersAction } from 'store/users/actions';

import VendorRequestDetailDrawer from '../VendorRequestDetailDrawer';
import VendorRequestsFilters from '../VendorRequestsFilters';
import VendorRequestsTable from '../VendorRequestsTable';

import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
  NO_STATUSES,
  SEARCH_DEBOUNCE_MS,
} from './VendorRequests.constants';
import { buildApiFilters } from './VendorRequests.utils';

const VendorRequests = () => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const groups = useSelector(selectLoanRequestGroups);
  const totalCount = useSelector(selectLoanRequestsTotalCount);
  const isLoading = useSelector(selectLoanRequestsIsLoading);

  // TableCDS keys rows on `row.id`; a group is identified by its fold key.
  const rows = useMemo(() => groups.map(group => ({ ...group, id: group.groupKey })), [groups]);

  const [page, setPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [statuses, setStatuses] = useState(NO_STATUSES);
  const [requestType, setRequestType] = useState('');
  const [requestTypeFilter, setRequestTypeFilter] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [vendorNameFilter, setVendorNameFilter] = useState('');
  const [createdAfter, setCreatedAfter] = useState(null);
  const [createdBefore, setCreatedBefore] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const isInitialFiltersRender = useRef(true);

  const setRequestTypeFilterDebounced = useDebounce(setRequestTypeFilter, SEARCH_DEBOUNCE_MS);
  const setVendorNameFilterDebounced = useDebounce(setVendorNameFilter, SEARCH_DEBOUNCE_MS);

  const apiFilters = useMemo(
    () =>
      buildApiFilters({
        statuses,
        requestType: requestTypeFilter,
        vendorName: vendorNameFilter,
        createdAfter,
        createdBefore,
      }),
    [statuses, requestTypeFilter, vendorNameFilter, createdAfter, createdBefore],
  );

  const hasActiveFilters = !_.isEmpty(apiFilters);

  const fetchData = useCallback(
    (nextPage, nextRowsPerPage) => {
      dispatch(
        searchLoanRequestGroupsAction({
          applicationId,
          filters: apiFilters,
          skip: nextPage * nextRowsPerPage,
          take: nextRowsPerPage,
        }),
      );
    },
    [applicationId, apiFilters, dispatch],
  );

  const onPageChange = useCallback(
    (_event, nextPage) => {
      setPage(nextPage);
      fetchData(nextPage, rowsPerPage);
    },
    [fetchData, rowsPerPage],
  );

  const onRowsPerPageChange = useCallback(
    event => {
      const nextRowsPerPage = parseInt(event.target.value, 10);

      setRowsPerPage(nextRowsPerPage);
      setPage(DEFAULT_PAGE);
      fetchData(DEFAULT_PAGE, nextRowsPerPage);
    },
    [fetchData],
  );

  const onStatusesChange = useCallback((_event, value) => setStatuses(value), []);

  const onRequestTypeChange = useCallback(
    event => {
      const { value } = event.target;

      setRequestType(value);
      setRequestTypeFilterDebounced(value);
    },
    [setRequestTypeFilterDebounced],
  );

  const onVendorNameChange = useCallback(
    event => {
      const { value } = event.target;

      setVendorName(value);
      setVendorNameFilterDebounced(value);
    },
    [setVendorNameFilterDebounced],
  );

  const onRowClick = useCallback(
    rowId => setSelectedGroup(rows.find(row => row.id === rowId) ?? null),
    [rows],
  );
  const onDrawerClose = useCallback(() => setSelectedGroup(null), []);

  useEffectSafeStrictMode(() => {
    if (_.isNil(applicationId)) {
      return;
    }

    dispatch(getUsersAction({ includeDeleted: true }));

    fetchData(DEFAULT_PAGE, DEFAULT_ROWS_PER_PAGE);
    setPage(DEFAULT_PAGE);
    setStatuses(NO_STATUSES);
    setRequestType('');
    setRequestTypeFilter('');
    setVendorName('');
    setVendorNameFilter('');
    setCreatedAfter(null);
    setCreatedBefore(null);

    return () => {
      dispatch(resetLoanRequestsAction());
    };
  }, [applicationId]);

  useEffectSafeStrictMode(() => {
    if (_.isNil(applicationId)) {
      return;
    }

    if (isInitialFiltersRender.current) {
      isInitialFiltersRender.current = false;

      return;
    }

    fetchData(DEFAULT_PAGE, rowsPerPage);
    setPage(DEFAULT_PAGE);
  }, [apiFilters]);

  const filtersNode = (
    <VendorRequestsFilters
      createdAfter={createdAfter}
      createdBefore={createdBefore}
      requestType={requestType}
      statuses={statuses}
      vendorName={vendorName}
      onCreatedAfterChange={setCreatedAfter}
      onCreatedBeforeChange={setCreatedBefore}
      onRequestTypeChange={onRequestTypeChange}
      onStatusesChange={onStatusesChange}
      onVendorNameChange={onVendorNameChange}
    />
  );

  return (
    <>
      <LoanTabLayout top={filtersNode} useBottomButtons={false}>
        <VendorRequestsTable
          hasActiveFilters={hasActiveFilters}
          isLoading={isLoading}
          items={rows}
          page={page}
          rowsPerPage={rowsPerPage}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onRowClick={onRowClick}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      </LoanTabLayout>
      <VendorRequestDetailDrawer
        applicationId={applicationId}
        group={selectedGroup}
        onClose={onDrawerClose}
      />
    </>
  );
};

export default memo(VendorRequests);
